import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { LargeClassCodeComboBoxModel } from './large-class-code-combo-box.model';
import { GetLargeClassListService } from './api-services/get-large-class-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 大分類コードコンボボックスを制御するクラス
 *
 * @export
 * @class LargeClassCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'app-large-class-code-combo-box',
  templateUrl: 'large-class-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: LargeClassCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetLargeClassListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetLargeClassListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class LargeClassCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  // 受注コード
  public selectedOrderCode: string = <string>'';
  // 部門コード
  public selectedClassCode: string = <string>'';
  // 大分類コード
  public selectedLargeClassCode: string = <string>'';
  // 大分類名
  public selectedLargeClassName: string = <string>'';
  public model: LargeClassCodeComboBoxModel = new LargeClassCodeComboBoxModel();

  public selectedIndex: number = <number>-1;

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() required: boolean = <boolean>false;
  @Input() orderCode: string = <string>'';
  @Input() classCode: string = <string>'';
  @Input() disabled: boolean = <boolean>false;
  @Input() isInitOrderCode: string = <string>'';
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() left: number = <number>10;
  @Input() top: number = <number>10;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;
  @Input() public classCodeComboBoxFlg: boolean = <boolean>false;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() forcedEnabledMode: string = null;

  /**
   * Creates an instance of LargeClassCodeComboBoxComponent.
   * @param {GetLargeClassListService} getLargeClassListService 大分類情報リストを取得する
   * @memberof LargeClassCodeComboBoxComponent
   */
  constructor(
    public elementRef: ElementRef,
    private getLargeClassListService: GetLargeClassListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedLargeClassCode) {
      this.selectedLargeClassCode = text;
      this.setLargeClassCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  ngOnInit(): void {
    // 大分類コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    this.reload();
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof LargeClassCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'classCode':
          this.classCode = changes.classCode.currentValue;
          if (!PlessViewUtilsSub.isEmpty(this.classCode)) {
            this.getData();
          } else {
            this.clear();
          }
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * コンボボックスの選択大分類コードを切り替える
   *
   * @param {string} largeClassCode 大分類コード
   * @memberof LargeClassCodeComboBoxComponent
   */
  public setLargeClassCode(largeClassCode: string): void {
    this.model.setLargeClassCode = largeClassCode;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedLargeClassCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  public updateSelectedLargeClassCode(): void {
    const orderList: Array<any> = this.model.largeClassList;

    for (let i = Number(0); i < orderList.length; i++) {
      if (PlessViewUtilsSub.trim(orderList[i].LargeClassCode) === this.model.setLargeClassCode) {
        this.selectedIndex = i;
        this.update();
      }
    }
    if (this.initFocusItem != null) {
      let queryedElement;
      if (this.initFocusItem instanceof HTMLElement) {
        queryedElement = this.initFocusItem.querySelector('textarea, input, select');
      } else {
        queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
      }
      (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
    }
  }

  /**
   * 更新
   *
   * @param {*} [e=null]
   * @returns {void}
   * @memberof LargeClassCodeComboBoxComponent
   */
  public update(e: any = null): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedClassCode = <string>'';
      this.selectedLargeClassCode = <string>'';
      this.selectedLargeClassName = <string>'';
      return;
    }

    // バインドフィールド設定
    this.selectedClassCode = this.model.largeClassList[Number(this.selectedIndex)].ClassCode;
    this.selectedLargeClassCode = this.model.largeClassList[Number(this.selectedIndex)].LargeClassCode;
    this.selectedLargeClassName = this.model.largeClassList[Number(this.selectedIndex)].LargeClassName;
    this.model.setLargeClassCode = <string>'';
  }

  /**
   * 条件にてコンボボックス内容を再ロードする
   *
   * @param {string} [classCode=<string>''] 部門コード
   * @param {string} [largeClassCode=<string>''] 大分類コード
   * @returns {void}
   * @memberof LargeClassCodeComboBoxComponent
   */
  public reload(classCode: string = <string>'', largeClassCode: string = <string>''): void {
    if (this.classCodeComboBoxFlg && PlessViewUtilsSub.isEmpty(classCode)) {
      this.clear();
      return;
    }

    // データ取得
    this.getLargeClassListService.execute(this.model, this.orderCode, this.classCode);

    // バインドフィールド初期化
    this.selectedClassCode = <string>'';
    this.selectedLargeClassCode = <string>'';
    this.selectedLargeClassName = <string>'';
  }

  /**
   * 初期化処理
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  public clear(): void {
    this.model = new LargeClassCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
    this.selectedOrderCode = <string>'';
    this.selectedClassCode = <string>'';
    this.selectedLargeClassCode = <string>'';
    this.selectedLargeClassName = <string>'';
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof LargeClassCodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.selectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.selectedIndex >= 0) {
      this.selectedIndex = this.model.selectedIndex;
      this.model.selectedIndex = <number>-1;
      this.update(null);
    }
  }

  /**
   * データ取得
   *
   * @memberof LargeClassCodeComboBoxComponent
   */
  public getData(): void {
    this.getLargeClassListService.execute(this.model, this.orderCode, this.classCode);
  }

}
